import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';

import Loader from 'components/Loader';
import {
  getSelectedCustomerID,
  getIsDefaultCustomer,
} from 'reducers/customerSearch';
import { getIssuedCoupons } from 'services/ErplyAPI';
import { IssuedCoupon } from 'types/Coupon';
import { applyCoupons } from 'actions/ShoppingCart';
import { getAppliedCoupons } from 'reducers/ShoppingCart';
import { ErplyApiError } from 'services/ErplyAPI/core/apiErrors';
import { addError } from 'actions/Error';

import Coupon from './Coupon';

const useApplyCoupons = (coupons: IssuedCoupon[]) => {
  const dispatch = useDispatch();
  useDebounce(() => dispatch(applyCoupons(coupons)), 500, [coupons]);
};

const IssuedCoupons: React.FC<{ id: string }> = ({ id }) => {
  const selectedCustomerID = useSelector(getSelectedCustomerID);
  const isDefaultCustomer = useSelector(getIsDefaultCustomer);
  const appliedCoupons = useSelector(getAppliedCoupons);
  const [issuedCoupons, setIssuedCoupons] = useState<IssuedCoupon[]>([]);
  const [checkedCouponIds, setCheckedCouponIds] = useState<string[]>(
    appliedCoupons.map(c => c.uniqueIdentifier),
  );
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const toggleCoupon = (id: string) =>
    setCheckedCouponIds(prevIds =>
      prevIds.includes(id)
        ? prevIds.filter(prevId => prevId !== id)
        : [...prevIds, id],
    );

  const removeIssuedCoupon = (id: string) =>
    setIssuedCoupons(prevCoupons =>
      prevCoupons.filter(c => c.uniqueIdentifier !== id),
    );

  const couponsToApply = useMemo(
    () =>
      issuedCoupons.filter(({ uniqueIdentifier }) =>
        checkedCouponIds.includes(uniqueIdentifier),
      ),
    [issuedCoupons, checkedCouponIds],
  );

  useApplyCoupons(couponsToApply);

  useDebounce(
    async () => {
      if (isDefaultCustomer) return Promise.resolve([]);
      try {
        setLoading(true);

        const coupons = await getIssuedCoupons({
          issuedCustomerID: selectedCustomerID,
          status: 'ACTIVE',
          ...(id ? { uniqueIdentifier: id } : {}),
        });

        setIssuedCoupons(coupons);
      } catch (error) {
        if (error instanceof ErplyApiError) {
          dispatch(addError(error.message));
        }
      } finally {
        setLoading(false);
      }
    },
    500,
    [isDefaultCustomer, selectedCustomerID, id],
  );

  return (
    <Loader show={loading} loadingText="Getting coupons..." block>
      {issuedCoupons?.map(coupon => (
        <Coupon
          key={coupon.uniqueIdentifier}
          coupon={coupon}
          checked={checkedCouponIds.includes(coupon.uniqueIdentifier)}
          toggleCoupon={toggleCoupon}
          removeIssuedCoupon={removeIssuedCoupon}
          used={appliedCoupons.find(c => c.uniqueIdentifier === coupon.uniqueIdentifier)?.used ?? false}
        />
      ))}
    </Loader>
  );
};

export default IssuedCoupons;
